import { StyledElement } from 'slate/components/StyledElement';
import { BlockTaggableList } from 'slate/components/BlockTaggableList';
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BookmarkElement } from 'slate/extensions/bookmark';
import { LinkElement } from 'slate/extensions/link';
import { StyledParagraphElement } from 'slate/extensions/styled-paragraph';
import { HighlightElement } from 'slate/extensions/highlight';
import { TagElement, ExcludeElement, FootnoteElement } from 'slate/extensions/tag';
import { ListItemElement } from 'slate/components/ListItemElement';
import { RenderElementProps } from 'slate-react';

export const Element = (props: RenderElementProps) => {
  const { attributes, children, element } = props;
  const elementUUID = useMemo(() => (element as { uuid?: string }).uuid || uuidv4(), [element]);
  const listId = `xrp-list-${elementUUID}`;

  // Add id to attributes if it does not exist
  const safeAttributesWithId = (attrs: RenderElementProps['attributes']) => ({
    ...attrs,
    id: (attrs as { id?: string }).id || listId,
  });
  switch (element.type) {
    case 'table':
      return (
        <table>
          <tbody style={{ userSelect: 'none' }} contentEditable={false} {...attributes}>
            {children}
          </tbody>
        </table>
      );
    case 'bulleted-list':
      return (
        <BlockTaggableList as="ul" attributes={safeAttributesWithId(attributes)} element={element} elementUUID={elementUUID}>
          {children}
        </BlockTaggableList>
      );
    case 'numbered-list':
      return (
        <BlockTaggableList as="ol" attributes={safeAttributesWithId(attributes)} element={element} elementUUID={elementUUID}>
          {children}
        </BlockTaggableList>
      );
    case 'list-item':
      return <ListItemElement {...props} />;
    case 'table-row':
      return <tr {...attributes}>{children}</tr>;
    case 'table-cell':
      return <td {...attributes}>{children}</td>;
    case 'paragraph':
      return <StyledElement {...props} />;
    case 'custom-formatting':
      return <StyledElement {...props} />;
    case 'bookmark':
      return <BookmarkElement {...props} element={element} />;
    case 'styled-paragraph':
      return <StyledParagraphElement {...props} element={element} />;
    case 'link':
      return <LinkElement {...props} element={element} />;
    case 'highlight':
      return <HighlightElement {...props} element={element} />;
    case 'tag':
      return <TagElement {...props} element={element} />;
    case 'exclude':
      return <ExcludeElement {...props} element={element} />;
    case 'footnote':
      return <FootnoteElement {...props} element={element} />;
    default:
      console.warn('No explicit type for render in editor found, using default element', props);
      return (
        <p className="default-editor-element" {...attributes}>
          {children}
        </p>
      );
  }
};
